import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardBody, 
  CardHeader,
  Input,
  Button,
  Divider,
  Chip,
  Spacer,
  Checkbox
} from '@heroui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [authMode, setAuthMode] = useState('firebase'); // 'firebase' or 'local'
  const { signup } = useAuth();
  const navigate = useNavigate();

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);
  const toggleConfirmPasswordVisibility = () => setIsConfirmPasswordVisible(!isConfirmPasswordVisible);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!agreedToTerms) {
      return setError('You must agree to the Terms of Service to sign up.');
    }

    if (password !== passwordConfirm) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password, authMode === 'local');
      navigate('/'); // Navigate to dashboard or home page after signup
    } catch (error) {
      setError('Failed to create an account: ' + (error.message || error));
    }

    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-gray-900 flex">
      {/* Left side - Welcome section */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white">
        <div className="max-w-md">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Join ApexionFlow
          </h1>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed">
            Start your journey with the most advanced reporting platform for law enforcement
          </p>

          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardBody className="p-6">
                <h3 className="text-xl font-semibold mb-4 text-white">What You'll Get</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="secondary" variant="flat">✓</Chip>
                    <span className="text-gray-300">Instant AI-powered assistance</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="secondary" variant="flat">✓</Chip>
                    <span className="text-gray-300">Professional review tools</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="secondary" variant="flat">✓</Chip>
                    <span className="text-gray-300">Secure cloud storage</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip size="sm" color="secondary" variant="flat">✓</Chip>
                    <span className="text-gray-300">24/7 support access</span>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardBody className="p-6">
                <h3 className="text-xl font-semibold mb-3 text-white">Trusted by Professionals</h3>
                <p className="text-gray-300 leading-relaxed">
                  Join thousands of law enforcement professionals who trust ApexionFlow 
                  for their critical reporting needs.
                </p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Right side - Signup form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl">
          <CardHeader className="flex flex-col gap-2 items-center pb-6">
            <h2 className="text-3xl font-bold text-gray-800">Create Account</h2>
            <p className="text-gray-600">Get started with ApexionFlow</p>
          </CardHeader>
          
          <CardBody className="space-y-6">
            {error && (
              <Chip color="danger" variant="flat" className="w-full p-3">
                {error}
              </Chip>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Auth Mode Toggle */}
              <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-4 border border-gray-200 dark:border-gray-700 shadow-inner">
                <button
                  type="button"
                  onClick={() => setAuthMode('firebase')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    authMode === 'firebase'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  Cloud Account
                </button>
                <button
                  type="button"
                  onClick={() => setAuthMode('local')}
                  className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    authMode === 'local'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  Local Account
                </button>
              </div>

              {authMode === 'local' && (
                <div className="text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/30 p-2.5 rounded-lg border border-purple-100 dark:border-purple-900/30 leading-relaxed mb-2">
                  ℹ️ <strong>Local Auth Mode:</strong> Stored on this device only. This user will only exist in your local browser storage.
                </div>
              )}

              <Input
                type="email"
                label="Email Address"
                placeholder={authMode === 'local' ? "local-dev@example.com" : "deputy@example.com"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="bordered"
                size="lg"
                isRequired
                classNames={{
                  input: "text-gray-800",
                  label: "text-gray-700",
                }}
              />

              <Input
                label="Password"
                placeholder="Create a secure password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="bordered"
                size="lg"
                isRequired
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                }
                type={isPasswordVisible ? "text" : "password"}
                classNames={{
                  input: "text-gray-800",
                  label: "text-gray-700",
                }}
              />

              <Input
                label="Confirm Password"
                placeholder="Confirm your password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                variant="bordered"
                size="lg"
                isRequired
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {isConfirmPasswordVisible ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                }
                type={isConfirmPasswordVisible ? "text" : "password"}
                classNames={{
                  input: "text-gray-800",
                  label: "text-gray-700",
                }}
              />

              <div className="py-2">
                <Checkbox
                  isSelected={agreedToTerms}
                  onValueChange={setAgreedToTerms}
                  size="sm"
                  classNames={{
                    label: "text-gray-700 text-sm",
                  }}
                >
                  I agree to the{' '}
                  <Link 
                    to="/terms" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                </Checkbox>
              </div>

              <Spacer y={2} />

              <Button
                type="submit"
                color="secondary"
                size="lg"
                className="w-full font-semibold"
                isLoading={loading}
                disabled={loading || !agreedToTerms}
              >
                {loading ? 'Creating Account...' : authMode === 'local' ? 'Create Local Account' : 'Create Account'}
              </Button>
            </form>

            <div className="text-center space-y-4">
              <Divider />
              
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Sign in
                </Link>
              </p>
            </div>

            <Divider />

            <div className="flex justify-center gap-4 text-sm">
              <Link 
                to="/terms" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Terms
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/privacy" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Privacy
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/security" 
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Security
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default Signup;